import { FeatureFlagContext, Features } from "@app/feature-flags";
import ProviderTerminationService from "@app/provider-terminations/providerTerminationService";
import { PageSection, LoadingSpinner } from "@app/shared";
import { InvalidNpiTableData, InvalidTinTableData, ResultsSummaryTableData, ReviewProjectsTableData } from "@app/shared/provider-change-wizards/interfaces";
import { QESAlert, QESCard, QESCheckbox, QESRadioButton, QESSelect, QESTextArea, QESTextInput } from "@app/shared/qes-standards";
import { ColumnConfig, Waffles } from "@app/shared/waffles";
import { Roles, UserContext } from "@app/user";
import { ChangeEvent, JSX, useContext, useEffect, useState } from "react";
import { TerminationsWizardContext } from "@app/terminations/import/TerminationsWizardContext";
import { TerminationsWizardStepProps, BulkProviderTerminationMethod } from "@app/terminations/import/interfaces";
import { wafflesKeyColumnName } from "@app/shared/waffles";
import { ClaimsActivityLineOfBusinessTypeEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";

const TerminationsWizardConfigureReport = (props: TerminationsWizardStepProps): JSX.Element => {
  const wizardContext = useContext(TerminationsWizardContext);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const userRoles = useContext(UserContext).roles;
  const providerTerminationService = new ProviderTerminationService();
  const [terminationReasons, setTerminationReasons] = useState<JSX.Element[]>([]);
  const [loadingGhostCounts, setLoadingGhostCounts] = useState((wizardContext.bulkProviderTerminationMethod == BulkProviderTerminationMethod.Ghost && !wizardContext.ghostCountsCalculated) ? true : false);
  const isDistinctServicingProviderReportEnabled = featureFlags.has(Features.DISTINCT_SERVICING_PROVIDER_REPORT) && userRoles.includes(Roles.DISTINCT_SERVICING_USER);

  const reviewColumnConfig: ColumnConfig = {
    details: {
      displayText: "",
      noFilter: true,
      noSort: true,
      testId: "details",
    },
    ...(wizardContext.bulkProviderTerminationMethod == BulkProviderTerminationMethod.Ghost && {
      ghostProviderCount: {
        displayText: `Ghost Count (LOB: ${wizardContext.selectedLineOfBusiness})`,
        noFilter: true,
        noSort: true,
        testId: "ghost-counts",
      },
    }),
    ...(wizardContext.bulkProviderTerminationMethod == BulkProviderTerminationMethod.Import && {
    invalidNpis: {
      displayText: `Invalid NPIs (${wizardContext.invalidNpis[0].invalidCount})`,
      noFilter: true,
      noSort: true,
      testId: "invalid-npis",
    },
    invalidTins: {
      displayText: `Invalid TINs (${wizardContext.invalidTins[0].invalidCount})`,
      noFilter: true,
      noSort: true,
      testId: "invalid-tins",
      },
    }),
    projectName: {
      displayText: `Projects (${wizardContext.reviewProjects.length})`,
      testId: "project-name",
    },
    validItems: {
      displayText: "Results Summary",
      noFilter: true,
      noSort: true,
      testId: "results-summary",
    },
  };

  useEffect(() => {
    providerTerminationService.getProviderTerminationReasons().then((response) => setTerminationReasons(response.map((r, i) => <option key={i} value={r}>{r}</option>)));
  }, []);

    useEffect(() => {
      if (wizardContext.bulkProviderTerminationMethod == BulkProviderTerminationMethod.Ghost && !wizardContext.ghostCountsCalculated) {
      setDataForGhostProviderTermination(wizardContext.selectedProjectIds, wizardContext.selectedLineOfBusiness);
    }
  }, []);

  useEffect(() => {
    if (wizardContext.bulkProviderTerminationMethod == BulkProviderTerminationMethod.Ghost) {
      props.formIsValid(!!wizardContext.terminationReason && (wizardContext.terminationReason !== "Other" || !!wizardContext.comment) && !!wizardContext.formatType && !!wizardContext.filename && !loadingGhostCounts);
    } else {
      props.formIsValid(!!wizardContext.terminationReason && (wizardContext.terminationReason !== "Other" || !!wizardContext.comment) && !!wizardContext.formatType && !!wizardContext.filename);
    }
  }, [wizardContext.terminationReason, wizardContext.comment, wizardContext.formatType, wizardContext.filename, loadingGhostCounts]);

  const setDataForGhostProviderTermination = async (projectIds: string[], lineOfBusiness: string) => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const response = await wizardContext.pciDashboardService.getGhostProviderCounts(projectIds, lineOfBusiness as ClaimsActivityLineOfBusinessTypeEnum);

        const reviewProjects = wizardContext.projects.filter((p) => wizardContext.selectedProjectIds.includes(p.projectId.toString()));
        reviewProjects.forEach(item => {
          item.ghostProviderCount = response.ghostNpiCountsByProject[parseInt(item.key)] ?? 0;
        });

        wizardContext.setReviewProjects(reviewProjects);

        const validItems: JSX.Element = (
          <span>{response.uniqueGhostNpiCount} Unique Ghost Providers (LOB: {wizardContext.selectedLineOfBusiness})</span>
        );
        wizardContext.setResultsSummary([{ validItems, [wafflesKeyColumnName]: "key" }]);
        wizardContext.setTerminateByNpi(true);
        wizardContext.setTerminateByTin(false);
        wizardContext.setTerminateByAllLocations(true);
        wizardContext.setTerminateByAllSpecialties(true);
        wizardContext.setGhostCountsCalculated(true);
        setLoadingGhostCounts(false);
        resolve();
      } catch (error) {
        reject(error.responseJSON);
      }
    });
  };

  return (
    <PageSection>
      <div className="container">
        <QESAlert type="warning" persist={false} classNames="mb-3">Your analysis will overwrite any existing comments and reasons for existing terminated providers in selected projects.</QESAlert>
        <div className="d-flex mb-3">
          <div className="col-6 pl-0 h-100">
            <QESCard title="Details" classNames="h-100">
              <QESSelect
                label="Termination Reason"
                placeholder="Termination Reason"
                required={true}
                testId="import-terminations__reason"
                value={wizardContext.terminationReason}
                onChange={(e) => wizardContext.setTerminationReason((e.target as HTMLSelectElement).value)}
                isValid={!!wizardContext.terminationReason}
              >
                {terminationReasons}
              </QESSelect>
              <QESTextArea
                label="Comments"
                placeholder="Type here to enter comments..."
                maxLength={100} testId="import-terminations__comments"
                required={wizardContext.terminationReason === "Other"}
                value={wizardContext.comment}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => wizardContext.setComment((e.target as HTMLTextAreaElement).value)}
                isValid={!!wizardContext.comment}
              />
            </QESCard>
          </div>
          <div className="col-6 pr-0">
            <QESCard title="Report" classNames="h-100">
              <QESTextInput label="Report Name" testId="import-terminations__export-name" value={wizardContext.filename} onChange={(e: ChangeEvent<HTMLInputElement>) => wizardContext.setFilename(e.target.value)} />
              <p className="mb-2">Report Type</p>
              <div className="pl-3 mb-3">
                <div className="mb-2">
                  {/* These radio button values need to match ExportFormatTypeEnum in the API */}
                  <QESRadioButton
                    label="CSV File"
                    name="report-type"
                    value="CSV"
                    checked={wizardContext.formatType === "CSV"}
                    onChange={(e) => wizardContext.setFormatType((e.target as HTMLInputElement).value)}
                    testId="import-terminations__format-csv"
                  />
                  <QESRadioButton
                    label="Pipe-delimited Text"
                    name="report-type"
                    id="pipe-delimited-text"
                    value="Pipe"
                    checked={wizardContext.formatType === "Pipe"}
                    onChange={(e) => wizardContext.setFormatType((e.target as HTMLInputElement).value)}
                    testId="import-terminations__format-pipe"
                  />
                  <QESRadioButton
                    label="Tab-delimited Text"
                    name="report-type"
                    id="tab-delimited-text"
                    value="Tab"
                    checked={wizardContext.formatType === "Tab"}
                    onChange={(e) => wizardContext.setFormatType((e.target as HTMLInputElement).value)}
                    testId="import-terminations__format-tab"
                  />
                </div>
              </div>
              <div className="pl-2 mt-2 mb-2">
                <div className="form-check">
                  <QESCheckbox
                    label="Alert me when the report is ready to view"
                    className="form-check-input"
                    id="alert-when-ready"
                    checked={wizardContext.emailOnSuccess}
                    onChange={(e) => wizardContext.setEmailOnSuccess((e.target as HTMLInputElement).checked)}
                    testId="import-terminations__email-me"
                  />
                </div>
                {isDistinctServicingProviderReportEnabled &&
                  <div>
                    <QESCheckbox
                      label="Distinct Servicing Providers"
                      checked={wizardContext.isDistinctServicingProviderReport}
                      onChange={(e) => wizardContext.setIsDistinctServicingProviderReport(e.target.checked)}
                      testId="import-terminations__distinct-servicing-provider-report"
                      containerClassName="mb-3"
                    />
                  </div>
                }
              </div>
            </QESCard>
          </div>
        </div>
        {wizardContext.bulkProviderTerminationMethod == BulkProviderTerminationMethod.Import &&
          <div className="mb-2">
            <Waffles<InvalidNpiTableData>
              columns={reviewColumnConfig}
              data={wizardContext.invalidNpis}
              fullWidth={true}
              testId="import-terminations-invalid-npis"
            />
          </div>
        }
        {wizardContext.bulkProviderTerminationMethod == BulkProviderTerminationMethod.Import &&
          <div className="mb-2">
            <Waffles<InvalidTinTableData>
              columns={reviewColumnConfig}
              data={wizardContext.invalidTins}
              fullWidth={true}
              testId="import-terminations-invalid-tins"
            />
          </div>
        }
        {loadingGhostCounts ?
          <PageSection>
            <div className="container">
              <LoadingSpinner />
            </div>
          </PageSection>
          :
          <>
            <div className="mb-2">
              <Waffles<ResultsSummaryTableData>
                columns={reviewColumnConfig}
                data={wizardContext.resultsSummary}
                fullWidth={true}
                testId="import-terminations-results-summary"
              />
            </div>
            <div className="mb-2">
              <Waffles<ReviewProjectsTableData>
                columns={reviewColumnConfig}
                data={wizardContext.reviewProjects}
                fullWidth={true}
                testId="import-terminations-review-projects"
              />
            </div>
          </>
        }
        <div className="mb-2">
          <div className="terminations-wizard__card-title">Reports</div>
          <div className="px-3">
            <QESCheckbox
              label="Project Details Termination Overview"
              checked={wizardContext.includedReports.projectDetailsTerminationOverview}
              onChange={(e) => { wizardContext.updateIncludedReports("projectDetailsTerminationOverview", e.target.checked) }}
              testId="import-terminations__project-details-termination-overview-checkbox"
            />
            <QESCheckbox
              label="Service Area Specialty Summary Changed Gaps"
              checked={wizardContext.includedReports.serviceAreaSpecialtySummaryChangedGaps}
              onChange={(e) => { wizardContext.updateIncludedReports("serviceAreaSpecialtySummaryChangedGaps", e.target.checked) }}
              testId="import-terminations__service-area-specialty-summary-changed-gaps-checkbox"
            />
            <QESCheckbox
              label="Service Area Specialty Summary Comparison"
              checked={wizardContext.includedReports.serviceAreaSpecialtySummaryComparison}
              onChange={(e) => { wizardContext.updateIncludedReports("serviceAreaSpecialtySummaryComparison", e.target.checked) }}
              testId="import-terminations__service-area-specialty-summary-comparison-checkbox"
            />
            <QESCheckbox
              label="Opportunity Specialty Summary Changed Gaps"
              checked={wizardContext.includedReports.opportunitySpecialtySummaryChangedGaps}
              onChange={(e) => { wizardContext.updateIncludedReports("opportunitySpecialtySummaryChangedGaps", e.target.checked) }}
              testId="import-terminations__opportunity-specialty-summary-changed-gaps-checkbox"
            />
            <QESCheckbox
              label="Opportunity Specialty Summary Comparison"
              checked={wizardContext.includedReports.opportunitySpecialtySummaryComparison}
              onChange={(e) => { wizardContext.updateIncludedReports("opportunitySpecialtySummaryComparison", e.target.checked) }}
              testId="import-terminations__opportunity-specialty-summary-comparison-checkbox"
            />
            {wizardContext.bulkProviderTerminationMethod == BulkProviderTerminationMethod.Import &&
              <QESCheckbox
                label="Termination Upload File"
                checked={wizardContext.includedReports.terminationUploadFile}
                onChange={(e) => { wizardContext.updateIncludedReports("terminationUploadFile", e.target.checked) }}
                testId="import-terminations__termination-upload-file-checkbox"
              />
            }
            {wizardContext.bulkProviderTerminationMethod == BulkProviderTerminationMethod.Ghost &&
              <QESCheckbox
                label="Ghost Termination List"
                checked={wizardContext.includedReports.ghostTerminationList}
                onChange={(e) => { wizardContext.updateIncludedReports("ghostTerminationList", e.target.checked) }}
                testId="ghost-terminations__ghost-termination-list-checkbox"
              />
            }
            <QESCheckbox
              label="Service Area Providers"
              checked={wizardContext.includedReports.serviceAreaProviders}
              onChange={(e) => { wizardContext.updateIncludedReports("serviceAreaProviders", e.target.checked) }}
              testId="import-terminations__service-area-providers-checkbox"
            />
            {wizardContext.bulkProviderTerminationMethod == BulkProviderTerminationMethod.Import &&
              <QESCheckbox
                label="Opportunity Providers"
                checked={wizardContext.includedReports.opportunityProviders}
                onChange={(e) => { wizardContext.updateIncludedReports("opportunityProviders", e.target.checked) }}
                testId="import-terminations__opportunity-providers-checkbox"
              />
            }
          </div>
        </div>
      </div>
    </PageSection>
  );
};

export default TerminationsWizardConfigureReport;
